'use client';

import { useParams } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import {
  PortalLink,
  tx,
  toFriendlyCourseSlug,
  useCurrentUser,
  useUserResourceSource,
  type PendingFormItem,
} from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import { SectionShell } from '../_shared/SectionShell';
import type { PaddingBox } from '../_shared/resolvePadding';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { ContentRail } from '../ui/ContentRail';

export interface PendingEvaluationFormsSliderProps {
  title?: string;
  limit?: number;
  itemWidth?: string;
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
}

const DEFAULT_LIMIT = 10;
const FORM_TYPE = 'EVALUATION';
const HREF_TEMPLATE = '/lxp/course/player/deliver/{slug}?type=evaluationForm';

function resolveHref(item: PendingFormItem): string {
  const slug = toFriendlyCourseSlug(item.courseTitle, item.courseId);
  return HREF_TEMPLATE.replace('{slug}', slug);
}

export function PendingEvaluationFormsSlider({
  title,
  limit = DEFAULT_LIMIT,
  itemWidth = 'var(--rail-item-width, 220px)',
  paddingOuter,
  paddingInner,
}: PendingEvaluationFormsSliderProps) {
  const { t } = useTranslation('common');
  const user = useCurrentUser();
  const ur = useUserResourceSource();
  const routeParams = useParams<{ view?: string | string[] }>();

  // Only the bare hub (`/lxp/courses`, no `[view]` segment) — never on a tab.
  const onHub = !routeParams?.view;

  const state = useAsyncResource<PendingFormItem[]>(
    async (signal) => {
      if (!user || !ur?.pendingForms || !onHub) return null;
      return await ur.pendingForms.fetch({ formType: FORM_TYPE, signal });
    },
    [user, ur, onHub],
  );

  if (!onHub) return null;
  if (!user || !ur?.pendingForms) return null;
  if (state.kind !== 'loaded' || state.data.length === 0) return null;

  const items = state.data.slice(0, limit);
  const resolvedTitle = title ? tx(title) : t('title.pendingEvaluationForms');
  const resultSubtitle = `${t('label.result')}: ${state.data.length}`;

  return (
    <SectionShell
      title={resolvedTitle}
      subtitle={resultSubtitle}
      paddingOuter={paddingOuter}
      paddingInner={paddingInner}
    >
      <style dangerouslySetInnerHTML={{ __html: PendingEvaluationFormsCss }} />
      <ContentRail ariaLabel={resolvedTitle} itemWidth={itemWidth} arrows="always" peek>
        {items.map((item) => (
          <Card asChild key={item.id}>
            <PortalLink href={resolveHref(item)} className="pefs-card">
              <div
                className="pefs-thumb"
                style={{
                  background: item.courseThumbnail
                    ? `url(${item.courseThumbnail}) center/cover no-repeat`
                    : 'linear-gradient(135deg, var(--color-brand-100, #dbeafe), var(--color-brand-50, #eff6ff))',
                }}
              >
                <span className="pefs-badge">{t('label.evaluationForm')}</span>
              </div>
              <div className="pefs-title">{item.courseTitle}</div>
            </PortalLink>
          </Card>
        ))}
      </ContentRail>
    </SectionShell>
  );
}

const PendingEvaluationFormsCss = `
/* Rendered as <Card>: border + elevation come from the component
   (ring-(--color-border-brand) + shadow-(--shadow-brand)). overflow:hidden
   stays — it rounds the thumbnail's top corners to the card radius. */
.pefs-card {
  display: block;
  text-decoration: none;
  border-radius: var(--radius-lg, 10px);
  overflow: hidden;
  background: var(--color-surface-card, #fff);
  transition: transform 0.2s ease;
}
.pefs-card:hover {
  transform: translateY(-2px);
}
.pefs-thumb {
  position: relative;
  aspect-ratio: var(--app-course-image-aspect-ratio, 16/9);
  background-size: cover;
  overflow: hidden;
}
.pefs-thumb::before {
  content: '';
  position: absolute;
  inset: 0;
  background: inherit;
  background-size: cover;
  background-position: center;
  transform: scale(1);
  transition: transform 0.3s ease;
}
.pefs-card:hover .pefs-thumb::before {
  transform: scale(1.06);
}
.pefs-badge {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: var(--space-1, 4px) var(--space-2, 8px);
  font-size: var(--font-size-xs, 12px);
  font-weight: 600;
  text-align: center;
  color: var(--color-text-on-brand, #fff);
  background: var(--color-brand-500, #319db3);
}
.pefs-title {
  padding: var(--space-2, 8px) var(--space-3, 12px);
  font-size: var(--font-size-sm, 14px);
  font-weight: 500;
  line-height: var(--line-height-tight, 1.35);
  color: var(--color-text-primary, #18181b);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
`;
